import { useAsync, useMountEffect } from "@react-hookz/web";
import { fetchQuery, graphql, useFragment, useRelayEnvironment } from "react-relay";

import { config } from "shared/data";

import type { hooksArticleReactionUtilsArticle$key } from "scmp-app/queries/__generated__/hooksArticleReactionUtilsArticle.graphql";
import type { hooksScmpArticleReactionConfigAppConfigQuery } from "scmp-app/queries/__generated__/hooksScmpArticleReactionConfigAppConfigQuery.graphql";

const useScmpArticleReactionConfig = () => {
  const environment = useRelayEnvironment();
  const [state, actions] = useAsync(async () => {
    const data = await fetchQuery<hooksScmpArticleReactionConfigAppConfigQuery>(
      environment,
      graphql`
        query hooksScmpArticleReactionConfigAppConfigQuery {
          appConfig(filter: { entityId: "scmp_pwa_article_reaction" }) {
            json
          }
        }
      `,
      {},
      {
        fetchPolicy: "store-or-network",
      },
    ).toPromise();

    if (!data?.appConfig?.json) return;
    const raw = data.appConfig.json as ScmpPwaArticleReactionAppConfig;
    return config.general.env === "production" ? raw.production : raw.dev;
  });

  useMountEffect(() => {
    void actions.execute();
  });

  return { scmpArticleReactionConfigState: state };
};

type ScmpPwaArticleReactionAppConfig = AppConfig<{
  disableWithArticleEntityIds: string[];
  disableWithSectionEntityIds: string[];
  disableWithTopicEntityIds: string[];
}>;

export const useArticleReactionUtils = (reference: hooksArticleReactionUtilsArticle$key) => {
  const article = useFragment(
    graphql`
      fragment hooksArticleReactionUtilsArticle on Article {
        sections {
          value {
            entityId
          }
        }
        topics {
          entityId
        }
        entityId
        sponsorType
      }
    `,
    reference,
  );
  const { scmpArticleReactionConfigState } = useScmpArticleReactionConfig();

  const hasSponsorType = article?.sponsorType !== null;

  const sectionEntityIds = article?.sections?.flatMap(section =>
    section?.value?.map(value => value && value.entityId),
  );
  const hasDisabledSection =
    scmpArticleReactionConfigState.result?.disableWithSectionEntityIds?.some(disabledEntityId =>
      sectionEntityIds?.includes(disabledEntityId),
    );

  const topicEntityIds = article?.topics?.flatMap(topic => topic?.entityId);
  const hasDisabledTopic = scmpArticleReactionConfigState.result?.disableWithTopicEntityIds?.some(
    disabledEntityId => topicEntityIds?.includes(disabledEntityId),
  );

  const hasDisabledArticle =
    scmpArticleReactionConfigState.result?.disableWithArticleEntityIds?.includes(article?.entityId);

  if (scmpArticleReactionConfigState.status !== "success") return { isEnabled: false };

  // There is couple of conditions that we need to disable article reaction
  // 1. Sponsor articles
  // 2. Check with app config
  return {
    isEnabled: !hasDisabledSection && !hasDisabledTopic && !hasDisabledArticle && !hasSponsorType,
  };
};
